import React from 'react';
import { useChatStore } from '../../store/chatStore';
import { ToolExecutionItem } from './ToolExecutionItem';
import type { ViewMode, PanelSize } from './types';
import { getRenderer } from '../tool-results/registry';
import type { ToolResultBlock } from '../../store/chatStore';

interface SelectedToolDetailsProps {
  selectedToolId: string | null;
  viewMode: ViewMode;
  panelSize: PanelSize;
}

export const SelectedToolDetails: React.FC<SelectedToolDetailsProps> =
  React.memo(
    ({ selectedToolId, viewMode, panelSize }: SelectedToolDetailsProps) => {
      const activeConversation = useChatStore((state) =>
        state.activeConversationId
          ? state.conversations[state.activeConversationId]
          : null,
      );

      const selectedExecution = React.useMemo(() => {
        if (!activeConversation || !selectedToolId) {
          return null;
        }

        // Find the tool use and its result
        let selectedToolUse = null;
        let selectedToolResult: ToolResultBlock | undefined;

        for (const message of activeConversation.messages) {
          for (const block of message.content) {
            if (block.type === 'toolUse' && block.id === selectedToolId) {
              selectedToolUse = block;
              const result = message.content.find(
                (r): r is ToolResultBlock =>
                  r.type === 'toolResult' && r.toolUseId === block.id,
              );
              if (result) {
                selectedToolResult = result;
              }
              break;
            }
          }
          if (selectedToolUse) {
            break;
          }
        }

        return selectedToolUse
          ? {
              toolUse: selectedToolUse,
              result: selectedToolResult,
            }
          : null;
      }, [activeConversation, selectedToolId]);

      if (!selectedToolId || !selectedExecution) {
        return null;
      }

      return (
        <div
          className={`flex-1 overflow-y-auto ${
            panelSize === 'fullscreen' ? 'container mx-auto' : ''
          }`}
        >
          <ToolExecutionItem
            toolUse={selectedExecution.toolUse}
            result={selectedExecution.result}
            viewMode={viewMode}
            getRenderer={getRenderer}
          />
        </div>
      );
    },
  );

SelectedToolDetails.displayName = 'SelectedToolDetails';
